/*
 * Copyright © HatioLab Inc. All rights reserved.
 *
 * GLTF fillStyle 타겟 노드 선택 에디터.
 * All / None / Custom 모드로 명시적 설정.
 * Custom 모드에서 개별 Mesh 체크박스 선택.
 */

import '@operato/i18n/ox-i18n.js'

import { css, html, PropertyValues, TemplateResult, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import { OxPropertyEditor, PropertySpec } from '@operato/property-editor'
import { ScrollbarStyles } from '@operato/styles/scrollbar-styles.js'

// fillStyleTargets 값 규칙:
// undefined 또는 미설정 → None (적용 안 함, 기본값)
// '*' → All (전체 메시 적용)
// ['mesh_0', ...] → Custom (선택 메시만 적용)

type FillMode = 'all' | 'none' | 'custom'

function getMode(value: any): FillMode {
  if (value === '*') return 'all'
  if (value === 'none' || !value) return 'none'
  if (Array.isArray(value) && value.length > 0) return 'custom'
  // [] (빈 배열) = custom with nothing selected
  if (Array.isArray(value)) return 'custom'
  return 'none'
}

@customElement('property-editor-gltf-fill-targets')
export default class GLTFFillTargetsEditor extends OxPropertyEditor {
  static styles = [
    ...OxPropertyEditor.styles,
    ScrollbarStyles,
    css`
      .mode-selector {
        display: flex;
        gap: 0;
        margin-bottom: 6px;
        border: 1px solid var(--md-sys-color-outline-variant, rgba(0, 0, 0, 0.15));
        border-radius: 6px;
        overflow: hidden;
      }

      .mode-btn {
        flex: 1;
        padding: 5px 0;
        font-size: 11px;
        font-weight: 500;
        text-align: center;
        cursor: pointer;
        background: var(--md-sys-color-surface-container, #f3edf7);
        color: var(--md-sys-color-on-surface, #1c1b1f);
        border: none;
        border-right: 1px solid var(--md-sys-color-outline-variant, rgba(0, 0, 0, 0.1));
        transition: all 0.15s;
      }

      .mode-btn:last-child {
        border-right: none;
      }

      .mode-btn:hover {
        background: var(--md-sys-color-surface-container-highest, #e6e0e9);
      }

      .mode-btn[active] {
        background: var(--md-sys-color-primary, #6750a4);
        color: var(--md-sys-color-on-primary, #fff);
      }

      .node-list {
        display: flex;
        flex-direction: column;
        gap: 1px;
        max-height: 200px;
        overflow-y: auto;
        font-size: 12px;
      }

      .node-item {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 4px 8px;
        cursor: pointer;
        border-radius: 4px;
        transition: background 0.1s;
      }

      .node-item:hover {
        background: var(--md-sys-color-primary-container, rgba(103, 80, 164, 0.12));
      }

      .node-item input[type='checkbox'] {
        margin: 0;
        accent-color: var(--md-sys-color-primary, #6750a4);
      }

      .node-item span {
        font-size: 12px;
        color: var(--md-sys-color-on-surface, #1c1b1f);
      }

      .mode-desc {
        font-size: 11px;
        color: var(--md-sys-color-on-surface-variant, #666);
        padding: 4px 0;
      }

      .mini-btn {
        font-size: 10px;
        padding: 2px 8px;
        border: 1px solid var(--md-sys-color-outline-variant, rgba(0, 0, 0, 0.15));
        border-radius: 4px;
        background: var(--md-sys-color-surface-container, #f3edf7);
        color: var(--md-sys-color-on-surface, #1c1b1f);
        cursor: pointer;
      }

      .mini-btn:hover {
        background: var(--md-sys-color-surface-container-highest, #e6e0e9);
      }

      .empty {
        color: var(--md-sys-color-on-surface-variant, #999);
        font-size: 11px;
        padding: 8px;
        text-align: center;
      }
    `
  ]

  @property({ type: String }) declare src: string | undefined

  @state() declare private _meshNames: string[]
  @state() declare private _mode: FillMode

  private _component: any = null
  private _lastExternalValue: any = undefined

  updated(changes: PropertyValues<this>) {
    if (changes.has('src')) {
      this.dispatchEvent(
        new CustomEvent('i-need-selected', {
          bubbles: true,
          composed: true,
          detail: {
            callback: (selected: any[]) => {
              this._component = selected?.[0]
              this._refreshMeshNames()
            }
          }
        })
      )
    }
  }

  private _refreshMeshNames() {
    if (!this._component) {
      this._meshNames = []
      return
    }

    const ro = this._component.realObject as any
    if (!ro?.getNode) {
      this._meshNames = []
      return
    }

    this._meshNames = ((this._component.nodeNames as string[]) ?? []).filter((name: string) => {
      const node = ro.getNode(name)
      return node?.isMesh
    })
  }

  editorTemplate(value: any, _spec: PropertySpec): TemplateResult {
    if (!this._meshNames?.length) {
      this._refreshMeshNames()
    }

    // 외부 value가 변경되면 모드 동기화
    if (value !== this._lastExternalValue) {
      this._lastExternalValue = value
      this._mode = getMode(value)
    }

    const mode = this._mode
    const meshCount = this._meshNames?.length ?? 0
    const targets: string[] = Array.isArray(this.value) ? this.value : (Array.isArray(value) ? value : [])

    return html`
      <fieldset fullwidth>
        ${meshCount > 0
          ? html`
              <div class="mode-selector">
                <div class="mode-btn" ?active=${mode === 'all'} @click=${() => this._setMode('all')}>All</div>
                <div class="mode-btn" ?active=${mode === 'none'} @click=${() => this._setMode('none')}>None</div>
                <div class="mode-btn" ?active=${mode === 'custom'} @click=${() => this._setMode('custom')}>Custom</div>
              </div>

              ${mode === 'all'
                ? html`<div class="mode-desc">${meshCount} meshes — fillStyle applies to all</div>`
                : nothing}
              ${mode === 'none'
                ? html`<div class="mode-desc">fillStyle not applied to any mesh</div>`
                : nothing}
              ${mode === 'custom'
                ? html`
                    <div style="display:flex;align-items:center;gap:6px">
                      <div class="mode-desc" style="flex:1">${targets.length} / ${meshCount} selected</div>
                      <button class="mini-btn" @click=${() => this._applyValue([...(this._meshNames ?? [])])}>All</button>
                      <button class="mini-btn" @click=${() => this._applyValue([])}>Clear</button>
                    </div>
                    <div class="node-list">
                      ${(this._meshNames ?? []).map(
                        name => html`
                          <label
                            class="node-item"
                            @mouseenter=${() => this._highlightNode(name)}
                            @mouseleave=${() => this._clearHighlight()}
                          >
                            <input
                              type="checkbox"
                              .checked=${targets.includes(name)}
                              @change=${(e: Event) => {
                                e.stopPropagation()
                                this._onToggle(name, (e.target as HTMLInputElement).checked, targets)
                              }}
                            />
                            <span>${name}</span>
                          </label>
                        `
                      )}
                    </div>
                  `
                : nothing}
            `
          : html`<div class="empty">
              <ox-i18n msgid="label.no-gltf-meshes">No meshes available</ox-i18n>
            </div>`}
      </fieldset>
    `
  }

  private _setMode(mode: FillMode) {
    const prevMode = this._mode
    this._mode = mode

    let newValue: any
    switch (mode) {
      case 'all':
        newValue = '*'
        break
      case 'none':
        newValue = 'none'
        break
      case 'custom':
        // 이전 상태에 따라: All → 전체 선택, None → 전체 해제
        if (prevMode === 'all') {
          newValue = [...(this._meshNames ?? [])]
        } else if (prevMode === 'none') {
          newValue = []
        } else {
          // 이미 custom이면 현재 값 유지
          newValue = Array.isArray(this.value) ? this.value : []
        }
        break
    }

    this._applyValue(newValue)
  }

  private _onToggle(name: string, checked: boolean, current: string[]) {
    const list = [...current]
    if (checked) {
      if (!list.includes(name)) list.push(name)
    } else {
      const idx = list.indexOf(name)
      if (idx >= 0) list.splice(idx, 1)
    }

    this._applyValue(list)
  }

  private _applyValue(newValue: any) {
    // 로컬 value 즉시 반영 (re-render)
    this.value = newValue
    this.requestUpdate()

    this.dispatchEvent(
      new CustomEvent('i-need-selected', {
        bubbles: true,
        composed: true,
        detail: {
          callback: (selected: any[]) => {
            selected[0].set('fillStyleTargets', newValue)
          }
        }
      })
    )
  }

  // --- Outline 강조 ---

  private _highlightNode(name: string) {
    const ro = this._component?.realObject as any
    if (!ro?.getNode) return

    const node = ro.getNode(name)
    if (!node) return

    this._getRendererManager()?.setOutlineObjects([node])
    this._component?.invalidate()
  }

  private _clearHighlight() {
    const ro = this._component?.realObject as any
    if (!ro) return

    this._getRendererManager()?.setOutlineObjects(ro.object3d ? [ro.object3d] : [])
    this._component?.invalidate()
  }

  private _getRendererManager(): any {
    const ro = this._component?.realObject as any
    const container = ro?.threeContainer as any
    return container?._capability?.rendererManager ?? container?._threeCapability?.rendererManager ?? null
  }
}
